import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { FileText, ScanSearch, Target, MessagesSquare, Users, ListChecks } from "lucide-react";
import SectionHeading from "../../components/ui/SectionHeading";
import { fadeUp } from "../../utils/animations";

const items = [
  { title: "Resume Builder", copy: "Build a recruiter-ready resume from guided, role-specific templates.", icon: FileText, emoji: "📝" },
  { title: "Resume Analyzer", copy: "Get instant feedback on formatting, keywords, and ATS readiness.", icon: ScanSearch, emoji: "🔍" },
  { title: "Job Matching", copy: "See roles matched to your skills, certifications, and experience.", icon: Target, emoji: "🎯" },
  { title: "Interview Preparation", copy: "Practice role-specific questions with structured prep material.", icon: MessagesSquare, emoji: "💬" },
  { title: "Mock Interviews", copy: "Run live mock interviews with mentors and get direct feedback.", icon: Users, emoji: "🎤" },
  { title: "Placement Tracking", copy: "Track every application and interview status in one place.", icon: ListChecks, emoji: "📊" },
];

export default function PlacementSection() {
  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        bgcolor: "#F1F5F9",
        py: { xs: 10, md: 14 },
        overflow: "hidden",
      }}
    >
      {/* ambient glow to match the premium EdTech feel */}
      <Box
        sx={{
          position: "absolute",
          top: -140,
          left: "50%",
          transform: "translateX(-50%)",
          width: 900,
          height: 500,
          background: "radial-gradient(closest-side, rgba(37,99,235,0.06), transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <div className="section-shell" style={{ position: "relative" }}>
        <Box sx={{ textAlign: "center", mx: "auto" }}>
          <SectionHeading
            align="center"
            eyebrow="Placement portal"
            title="A career operating system for students and professionals."
            copy="Resume builder, resume analyzer, job matching, interview preparation, mock interviews, and placement tracking — one guided workflow from first draft to first offer."
          />
        </Box>

        <Box sx={{ mt: { xs: 8, md: 10 } }}>
          {/* horizontal scroll wrapper keeps all 6 steps on one row at every screen size */}
          <Box sx={{ overflowX: { xs: "auto", md: "visible" }, pb: { xs: 3, md: 0 } }}>
            <Box sx={{ position: "relative", minWidth: { xs: 1260, md: "auto" } }}>
              {/* connecting line through icon centers */}
              <Box
                sx={{
                  position: "absolute",
                  top: 34,
                  left: "8.3%",
                  right: "8.3%",
                  height: "2px",
                  bgcolor: "rgba(37,99,235,0.18)",
                  zIndex: 0,
                }}
              />

              <Grid container wrap="nowrap" spacing={{ xs: 2.5, md: 2.5 }} sx={{ position: "relative", zIndex: 1 }}>
                {items.map(({ title, copy, emoji }, i) => (
                  <Grid item xs={2} key={title} sx={{ minWidth: 0 }}>
                    <motion.div {...fadeUp(i * 0.06)} style={{ height: "100%" }}>
                      <Box
                        className="placement-node"
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          textAlign: "center",
                          height: "100%",
                        }}
                      >
                        {/* icon circle with number badge */}
                        <Box sx={{ position: "relative", flexShrink: 0, width: 68, height: 68, mb: 2.5 }}>
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              mx: "auto",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "#EFF4FF",
                              border: "1px solid rgba(37,99,235,0.18)",
                              boxShadow: "0 10px 24px -14px rgba(37,99,235,0.35)",
                              transition: "all 0.3s ease",
                              ".placement-node:hover &": {
                                bgcolor: "primary.main",
                                transform: "scale(1.06)",
                                boxShadow: "0 0 0 6px rgba(37,99,235,0.14), 0 14px 28px -12px rgba(37,99,235,0.55)",
                              },
                            }}
                          >
                            <Typography component="span" sx={{ fontSize: 26, lineHeight: 1 }}>
                              {emoji}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              position: "absolute",
                              top: -4,
                              left: "50%",
                              transform: "translateX(-50%)",
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                              bgcolor: "#0B1530",
                              color: "white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 11,
                              fontWeight: 800,
                              border: "2px solid #F1F5F9",
                              boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
                            }}
                          >
                            {i + 1}
                          </Box>
                        </Box>

                        {/* card */}
                        <Box
                          sx={{
                            width: "100%",
                            flex: 1,
                            p: { xs: 2.5, md: 2.75 },
                            borderRadius: "18px",
                            border: "1px solid rgba(15,23,42,0.08)",
                            bgcolor: "white",
                            boxShadow: "0 2px 10px rgba(15,23,42,0.04)",
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            transition:
                              "transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease, background-color 0.28s ease",
                            "&:hover": {
                              transform: "translateY(-4px)",
                              boxShadow: "0 24px 46px -20px rgba(37,99,235,0.4)",
                              borderColor: "rgba(37,99,235,0.5)",
                              bgcolor: "#F6F9FF",
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.66rem",
                              fontWeight: 700,
                              color: "primary.main",
                              letterSpacing: "0.08em",
                              mb: 0.75,
                              textAlign: "center",
                            }}
                          >
                            STEP {String(i + 1).padStart(2, "0")}
                          </Typography>
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            gutterBottom
                            sx={{ letterSpacing: "-0.01em", textAlign: "center", fontSize: "0.86rem" }}
                          >
                            {title}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            lineHeight={1.65}
                            sx={{ display: "block", maxWidth: 170, mx: "auto", textAlign: "center" }}
                          >
                            {copy}
                          </Typography>

                          {/* progress dots */}
                          <Box sx={{ display: "flex", gap: 0.6, mt: 2 }}>
                            {items.map((_, d) => (
                              <Box
                                key={d}
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  bgcolor: d <= i ? "primary.main" : "rgba(15,23,42,0.15)",
                                  transition: "background-color 0.3s ease",
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        </Box>
      </div>
    </Box>
  );
}